import React, { Component } from "react";
import { connect } from "react-redux";
import { Dispatch, bindActionCreators } from "redux";

import cx from "classnames";
import contentfulImgUtil from "utils/contentfulImgUtil";

import getUpdatedLineItems from "utils/getUpdatedLineItems";
import getRemovedLineItems from "utils/getRemovedLineItems";

import { closeMiniCart } from "state/actions/ui/miniCartUIactions";
import { updateLineItems } from "state/actions/checkoutActions";

import cartLineItems from "state/selectors/cartLineItems";

import { Image, Button } from "components/base";

import { MinniemuseShopRoutes } from "constants/Routes";

import { GlobalState, Checkout, CartLineItems, UpdatingLineItems } from "types";

interface StoreProps {
  miniCartIsOpen: boolean;
  checkout: Checkout;
  cartLineItems: CartLineItems;
}

interface DispatchProps {
  actions: {
    closeMiniCart(): void;
    updateLineItems(checkoutId: string, lineItems: UpdatingLineItems): void;
  };
}

type Props = StoreProps & DispatchProps;

const closeCartAriaLabel = "Close mini cart";

class MiniCart extends Component<Props> {
  componentDidUpdate(prevProps: Props) {
    if (!prevProps.miniCartIsOpen && this.props.miniCartIsOpen) {
      const closeButton: HTMLButtonElement = document.getElementsByClassName(
        "MiniCart__close-button"
      )[0] as HTMLButtonElement;
      closeButton.focus();
    }
  }

  handleQuantityChange = (variantId: string, quantity: number) => {
    const { checkout, actions } = this.props;

    const updatedLineItems = getUpdatedLineItems(checkout.lineItems, {
      variantId: variantId,
      quantity: quantity
    });

    actions.updateLineItems(checkout.id, updatedLineItems);
  };

  handleRemoveItem = (variantId: string) => {
    const { checkout, actions } = this.props;

    actions.updateLineItems(
      checkout.id,
      getRemovedLineItems(checkout.lineItems, variantId)
    );
  };

  render() {
    const { checkout, actions, cartLineItems, miniCartIsOpen } = this.props;
    const cartItemsLength = cartLineItems.length;
    const windowIsLongerThanCart = window.innerHeight >= 651;

    return (
      <div
        className={cx(
          "MiniCart fixed t0 r0 bg-color-white z-overlay flex flex-col justify-between transition",
          {
            "MiniCart--active": miniCartIsOpen
          }
        )}
      >
        <div className="MiniCart__cart-title-container MiniCart__content-padding flex justify-between">
          <p className="color-gray-dark text-md gt-america uppercase text-bold">{`Your bag (${checkout.totalLineItemsCount})`}</p>
          <Button
            className="MiniCart__close-button bg-color-transparent p0"
            ariaLabel={closeCartAriaLabel}
            onClick={actions.closeMiniCart}
          >
            <Image
              alt={closeCartAriaLabel}
              src="/assets/images/close-icon.svg"
            />
          </Button>
        </div>
        <div
          className={cx("MiniCart__line-items-container w100", {
            "overflow-y-scroll": cartItemsLength > 2 || !windowIsLongerThanCart,
            "overflow-hidden": cartItemsLength <= 2 && windowIsLongerThanCart
          })}
        >
          {!cartItemsLength && (
            <p className="p2 mxauto text-center color-gray-dark text-md gt-america uppercase">
              Your cart is empty
            </p>
          )}
          {cartLineItems.map(cartLineItem => {
            const productHandle = cartLineItem.productHandle;
            const productDetailRoute =
              MinniemuseShopRoutes.PRODUCT_DETAIL.base + productHandle;
            const productButtonAriaLabel = `Navigate to ${cartLineItem.title} detail page`;
            const variantId = cartLineItem.variantId;

            return (
              <div
                key={variantId}
                className="MiniCart__line-item-container flex flex-col md:flex-row md:items-center"
              >
                <Button
                  containerClassName="MiniCart__line-item-image mxauto"
                  className="w100 relative p0 bg-color-transparent"
                  to={productDetailRoute}
                  onClick={actions.closeMiniCart}
                  ariaLabel={productButtonAriaLabel}
                >
                  <Image
                    className="aspect-square w100"
                    style={{
                      backgroundSize: "cover",
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "center"
                    }}
                    isBg={true}
                    alt={productButtonAriaLabel}
                    src={contentfulImgUtil(cartLineItem.image.url, "400")}
                  />
                </Button>
                <div className="w100 md:ml1_5 text-xs color-gray-dark gt-america uppercase text-bold">
                  <div className="MiniCart__line-item-title-container w100 flex justify-between">
                    <Button
                      className="bg-color-transparent color-gray-dark"
                      to={productDetailRoute}
                      onClick={actions.closeMiniCart}
                      ariaLabel={productButtonAriaLabel}
                    >
                      <p>{cartLineItem.prefix}</p>
                      <p>{cartLineItem.title}</p>
                    </Button>
                    <p>{`$${cartLineItem.price.amount}`}</p>
                  </div>
                  <div className="w100 color-gray flex items-end">
                    <div className="w100">
                      {cartLineItem.productOptions.map(option => {
                        const value = option.values.find(value => {
                          return cartLineItem.variantOptions.includes(value);
                        });
                        return (
                          <p key={option.name}>{`${option.name}: ${value}`}</p>
                        );
                      })}
                      <div className="w100 flex justify-between">
                        <div className="flex items-center">
                          <p className="mr_75">Quantity:</p>
                          <Button
                            className="color-gray uppercase text-xs text-bold p0 bg-color-transparent"
                            ariaLabel="Subtract 1 from quantity"
                            label="-"
                            onClick={() =>
                              this.handleQuantityChange(variantId, -1)
                            }
                          />
                          <p className="MiniCart__quantity flex justify-center">
                            {cartLineItem.quantity}
                          </p>
                          <Button
                            className="color-gray uppercase text-xs text-bold p0 bg-color-transparent"
                            ariaLabel="Add 1 to quantity"
                            label="+"
                            onClick={() =>
                              this.handleQuantityChange(variantId, 1)
                            }
                          />
                        </div>
                        <Button
                          className="color-gray uppercase text-xs text-bold p0 bg-color-transparent"
                          ariaLabel="Remove this item from cart"
                          label="Remove"
                          onClick={() => this.handleRemoveItem(variantId)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="MiniCart__content-padding">
          <div className="MiniCart__subtotal-conatiner color-gray-dark text-md gt-america uppercase text-bold flex justify-between">
            <p>Subtotal</p>
            <p>{`$${checkout.subtotalPrice.amount}`}</p>
          </div>
          <Button
            className="MiniCart__checkout-button bg-color-orange color-white transition uppercase gt-america text-sm text-bold flex items-center justify-center"
            variant="primary"
            ariaLabel="Checkout"
            label="Checkout"
            to={checkout.webUrl}
          />
          <p className="MiniCart__info uppercase gt-america text-center">
            shipping and discount codes are added at checkout
          </p>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: GlobalState) => ({
  checkout: state.session.checkout.checkout,
  miniCartIsOpen: state.miniCartUI.miniCartIsOpen,
  cartLineItems: cartLineItems(state)
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: bindActionCreators(
    {
      closeMiniCart,
      updateLineItems
    },
    dispatch
  )
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MiniCart);
